import { postSuggestedQuestions } from 'api/v2/AI';
import cn from 'classnames';
import Button from 'components/atoms/Button/Button.component';
import { Status } from 'components/molecules/FileUploadedList/FileUploaded.types';
import { useAIChatContext } from 'components/organisms/AIChat/AIChat.context';
import { useCallback, useEffect, useState, type FC } from 'react';
import { useMutation } from 'react-query';
import { SuggestedQuestion } from 'types/ai.types';
import { logAmplitude } from 'utils/amplitude';
import { WEB_AIASSISTANT_CLICKSUGGESTEDQUESTION } from 'utils/amplitude/events/ai.amplitude';
import SuggestedQuestionIcon from './SuggestedQuestionIcon';

type SuggestedQuestionCardProps = {
  question: SuggestedQuestion;
  horizontal?: boolean;
  index: number;
};

const SuggestedQuestionCard: FC<SuggestedQuestionCardProps> = ({
  question,
  horizontal = false,
  index,
}) => {
  const { handleSend, attachmentApi } = useAIChatContext();

  const [isFilePickerOpen, setIsFilePickerOpen] = useState(false);

  const postSuggestedQuestion = useMutation(
    () => postSuggestedQuestions(question.id, index, question.prompt_metadata),
    { onSuccess: () => handleSend(question.text) },
  );

  const onClick = useCallback(() => {
    logAmplitude(WEB_AIASSISTANT_CLICKSUGGESTEDQUESTION, {
      promptId: question.id,
      index: index.toString(),
    });

    if (question.required_metadata) {
      setIsFilePickerOpen(true);
      attachmentApi?.open();
    } else {
      postSuggestedQuestion.mutate();
    }
  }, [postSuggestedQuestion, handleSend, setIsFilePickerOpen, question.text]);

  useEffect(() => {
    if (attachmentApi?.attachment?.status === Status.done && isFilePickerOpen) {
      setIsFilePickerOpen(false);
      postSuggestedQuestion.mutate();
    }
  }, [
    attachmentApi?.attachment?.status,
    isFilePickerOpen,
    question.text,
    postSuggestedQuestion,
  ]);

  return (
    <Button
      asDefault
      onClick={onClick}
      className={cn(
        'bg-white rounded-xl p-3 border border-primary-100 max-w-[340px] flex-shrink-0 text-left',
        {
          'max-w-[340px]': horizontal,
          'max-w-[270px]': !horizontal,
        },
      )}
    >
      <div
        className={cn('flex items-start gap-2 justify-start h-full', {
          'flex-row': horizontal,
          'flex-col': !horizontal,
        })}
      >
        <SuggestedQuestionIcon icon={question.icon} color={question.color} />
        <p className="line-clamp-3 w-full text-sm leading-5 break-words">
          {question.text}
        </p>
      </div>
    </Button>
  );
};

export default SuggestedQuestionCard;
